// Spark's shared helpers: access flags, dates, errors, a project's status word. Every
// visual piece comes from the kit (components/ui); colour lists live in sparkRules.ts.

import React from 'react';
import { Button } from '../components/ui/Button';
import useUserStore from '../stores/useUserStore';
import { formatEventDate } from '../utils/date-utils';
import type { ProductionRow } from './sparkApi';
import { STATUS_REASONS } from './sparkRules';

export const errorMessage = (e: unknown, fallback = 'Something went wrong. Try again.'): string => {
  const err = e as { message?: string } | null;
  return err?.message || fallback;
};

export const errorCode = (e: unknown): string | undefined =>
  (e as { data?: { error?: { code?: string } } } | null)?.data?.error?.code;

export interface SparkAccess {
  userId: string;
  /** Edits projects, deliverables, timers, reviews. */
  studio: boolean;
  /** Draws and approves show teams. */
  approver: boolean;
}

export function useSparkAccess(): SparkAccess {
  const user = useUserStore((s: any) => s.user);
  const admin = !!(user?.isadmin || user?.can_admin);
  return {
    userId: user?.id || '',
    studio: admin || !!user?.can_animator,
    approver: admin || !!(user?.can_animator && user?.can_manager),
  };
}

export const dayText = (ymd: string | null | undefined, fmt = 'MMM D, YYYY'): string =>
  ymd ? formatEventDate(ymd, fmt) : '';

/** The date a project row shows: Ongoing for the standing project, else its show date. */
export const rowDateText = (row: Pick<ProductionRow, 'is_standing' | 'show_date'>): string =>
  row.is_standing ? 'Ongoing' : row.show_date ? dayText(row.show_date, 'MMM D') : 'No date';

export const ymdValue = (v: string | null | undefined): string => (v ? v.slice(0, 10) : '');

/** The word and colour a project's status shows, or null when it has none. */
export const statusOf = (row: Pick<ProductionRow, 'status_reason' | 'archived_at' | 'archived_reason' | 'flown'>): { label: string; color: string } | null => {
  if (row.archived_at) return { label: 'Cancelled', color: '#fca5a5' };
  if (row.flown) return { label: 'Flown', color: '#bef264' };
  if (row.status_reason) {
    return { label: row.status_reason, color: STATUS_REASONS.find((s) => s.label === row.status_reason)?.color || '#bbbbbb' };
  }
  return null;
};

/** An error with a way out. */
export const LoadError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div role="alert" className="flex flex-wrap items-center gap-3 rounded-panel bg-red-500/10 px-4 py-3 text-sm text-on-surface">
    <span className="min-w-0 flex-1">{message}</span>
    <Button variant="tonal" onClick={onRetry}>Try again</Button>
  </div>
);
